import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ToolId } from 'config/toolDefinitionsConfig';
import {
  applyRegulatorConnectSelection,
  clearConnect2DSelection,
  clearRegulatorConnectSelection,
  setRegulatorConnectEvents,
  subscribeRegulatorConnectSelection,
} from '../../rendering/Panels/Renderers/CornerstoneManager';
import type { RegulatorConnectSelectionState } from '../../rendering/Panels/Renderers/CornerstoneManager';
import { ModifierType } from '@features/analysis/segmentManager';

type RegulatorConfig = {
  regulatorConnectThickness?: number;
  regulatorConnectHuEnabled?: boolean;
  regulatorConnectHuMin?: number;
  regulatorConnectHuMax?: number;
};

type SetConfigData = (data: Record<string, unknown>) => void;

export function useRegulatorConnectController({
  config,
  setData,
  selectedSeriesId,
  lowQuality,
  manualMode,
  showRegulatorConnectTools,
  enabled,
  modifierType,
  activeToolId,
  handleToolActivate,
  setMode,
  setDirty
}: {
  config: RegulatorConfig;
  setData: SetConfigData;
  selectedSeriesId?: string | null;
  lowQuality: boolean;
  manualMode: boolean;
  showRegulatorConnectTools: boolean;
  enabled:boolean;
  modifierType: ModifierType;
  activeToolId:ToolId | undefined;
  handleToolActivate: (toolId: ToolId) => void;
  setMode: (value: boolean) => void;
  setDirty: (value: boolean) => void;
}) {
  const thickness = Math.max(
    1,
    Math.min(20, Number(config.regulatorConnectThickness) || 3),
  );
  const huEnabled = !!config.regulatorConnectHuEnabled;
  const huMin = Number.isFinite(Number(config.regulatorConnectHuMin))
    ? Number(config.regulatorConnectHuMin)
    : -1024;
  const huMax = Number.isFinite(Number(config.regulatorConnectHuMax))
    ? Number(config.regulatorConnectHuMax)
    : 3071;

  const [thicknessText, setThicknessText] = useState(String(thickness));
  const [huMinText, setHuMinText] = useState(String(huMin));
  const [huMaxText, setHuMaxText] = useState(String(huMax));
  const [status, setStatus] = useState<RegulatorConnectSelectionState>({
    start: null,
    end: null,
    ready: false,
  });
  const [prevManual2DTool, setPrevManual2DTool] = useState<ToolId | undefined>(
    'Scroll',
  );

  useEffect(() => subscribeRegulatorConnectSelection(setStatus), []);

  useEffect(() => {
    setThicknessText(String(thickness));
  }, [thickness]);

  useEffect(() => {
    setHuMinText(String(huMin));
  }, [huMin]);

  useEffect(() => {
    setHuMaxText(String(huMax));
  }, [huMax]);

  const disableAndClear = useCallback(() => {
    handleToolActivate('Scroll');
    clearRegulatorConnectSelection();
  }, []);

  const stopInputPropagation = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      event.stopPropagation();
    },
    [],
  );

  const handleInputWheel = useCallback(
    (event: React.WheelEvent<HTMLInputElement>) => {
      event.currentTarget.blur();
      event.stopPropagation();
    },
    [],
  );

  const handleToggle = useCallback(() => {
    const next = !enabled;
    if (next) {
      setPrevManual2DTool(activeToolId);
      handleToolActivate('connectPoint');
      setMode(true);
    } else {
      handleToolActivate(prevManual2DTool?? 'Scroll');
      clearRegulatorConnectSelection();
    }
  }, [enabled, handleToolActivate]);

  const handleThicknessChange = useCallback(
    (value: number) => {
      const clamped = Math.max(1, Math.min(20, Math.round(value || 1)));
      setThicknessText(String(clamped));
      setData({ regulatorConnectThickness: clamped });
    },
    [setData],
  );

  const handleThicknessTextChange = useCallback(
    (value: string) => {
      setThicknessText(value);
      if (value.trim() === '') return;
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return;
      const clamped = Math.max(1, Math.min(20, Math.round(parsed)));
      setData({ regulatorConnectThickness: clamped });
    },
    [setData],
  );

  const handleThicknessBlur = useCallback(() => {
    const parsed = Number(thicknessText);
    const clamped = Math.max(
      1,
      Math.min(20, Math.round(Number.isFinite(parsed) ? parsed : thickness)),
    );
    setThicknessText(String(clamped));
    setData({ regulatorConnectThickness: clamped });
  }, [setData, thickness, thicknessText]);

  const handleHuEnabledChange = useCallback(
    (nextEnabled: boolean) => {
      setData({ regulatorConnectHuEnabled: nextEnabled });
    },
    [setData],
  );

  const handleHuRangeTextChange = useCallback(
    (key: 'regulatorConnectHuMin' | 'regulatorConnectHuMax', value: string) => {
      if (key === 'regulatorConnectHuMin') {
        setHuMinText(value);
      } else {
        setHuMaxText(value);
      }

      const trimmed = value.trim();
      if (trimmed === '' || trimmed === '-' || trimmed === '+') return;

      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) return;

      setData({ [key]: Math.round(parsed) });
    },
    [setData],
  );

  const normalizeHuRange = useCallback(() => {
    const parsedMin = Number(huMinText);
    const parsedMax = Number(huMaxText);
    const safeMin = Number.isFinite(parsedMin) ? Math.round(parsedMin) : huMin;
    const safeMax = Number.isFinite(parsedMax) ? Math.round(parsedMax) : huMax;
    const nextMin = Math.min(safeMin, safeMax);
    const nextMax = Math.max(safeMin, safeMax);

    setHuMinText(String(nextMin));
    setHuMaxText(String(nextMax));
    setData({
      regulatorConnectHuMin: nextMin,
      regulatorConnectHuMax: nextMax,
    });
  }, [huMax, huMaxText, huMin, huMinText, setData]);

  const handleApply = useCallback(() => {
    if (!selectedSeriesId) return;
    clearConnect2DSelection();
    const result = applyRegulatorConnectSelection({
      seriesId: selectedSeriesId,
      lowQuality,
      thickness,
      huFilter: huEnabled
        ? {
            enabled: true,
            min: Math.min(huMin, huMax),
            max: Math.max(huMin, huMax),
          }
        : { enabled: false },
    });
    if (result) {
      console.log('[Regulator Connect][UI Apply]', result);
    }
  }, [huEnabled, huMax, huMin, lowQuality, selectedSeriesId, thickness]);

  useEffect(() => {
    if (!showRegulatorConnectTools) {
      if (enabled) {
        handleToolActivate('Scroll');
      };
      clearRegulatorConnectSelection();
      return;
    }
    if (!selectedSeriesId || !manualMode || !enabled) return;

    const removers = setRegulatorConnectEvents({
      seriesId: selectedSeriesId,
      lowQuality,
    });
    return () => {
      removers.forEach((func) => func?.());
    };
  }, [modifierType, enabled, lowQuality, manualMode, selectedSeriesId, showRegulatorConnectTools]);

  return {
    enabled,
    status,
    thickness,
    thicknessText,
    huEnabled,
    huMin,
    huMax,
    huMinText,
    huMaxText,
    disableAndClear,
    clearSelection: clearRegulatorConnectSelection,
    handleToggle,
    handleApply,
    handleThicknessChange,
    handleThicknessTextChange,
    handleThicknessBlur,
    handleHuEnabledChange,
    handleHuRangeTextChange,
    normalizeHuRange,
    stopInputPropagation,
    handleInputWheel,
  };
}
